import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import './AdminLogin.css';

function AdminLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(''); // Clear error on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;

      // Store user data
      const userData = {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified
      };

      if (formData.rememberMe) {
        localStorage.setItem('adminUser', JSON.stringify(userData));
      } else {
        sessionStorage.setItem('adminUser', JSON.stringify(userData));
      }

      // Redirect to dashboard
      navigate('/admin/dashboard');
    } catch (err) {
      let errorMessage = 'An error occurred during login';
      
      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        default:
          errorMessage = err.message || 'Login failed';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-body">
        <div className="admin-main">
          <div className="admin-content">
            {/* Header Section */}
            <div className="admin-header">
              <div className="admin-logo">
                <img
                  src={process.env.PUBLIC_URL + '/logo.jpg'}
                  alt="Mountain Top University"
                  className="admin-logo-image"
                />
              </div>
              <h1 className="admin-title">Admin Portal</h1>
              <p className="admin-subtitle">Sign in to access your dashboard</p>
            </div>

            {/* Login Form */}
            <div className="admin-form-container">
              {error && (
                <div className="admin-error-message">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="admin-form">
                {/* Email Field */}
                <div className="admin-form-field">
                  <label htmlFor="email">Email Address</label>
                  <div className="admin-input-wrapper">
                    <div className="admin-input-icon">
                      <EmailIcon />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="admin@company.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="admin-form-field">
                  <label htmlFor="password">Password</label>
                  <div className="admin-input-wrapper">
                    <div className="admin-input-icon">
                      <LockIcon />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      className="admin-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="admin-form-options">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                    />
                    <span>Remember me</span>
                  </label>
                  <button type="button" className="admin-forgot-link" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}>Forgot password?</button>
                </div>

                {/* Login Button */}
                <button type="submit" className="admin-login-button" disabled={loading}>
                  <span>{loading ? 'Logging in...' : 'Login'}</span>
                  {!loading && <ArrowRightIcon />}
                </button>
              </form>

              {/* Security Footer */}
              <div className="admin-security-footer">
                <LockSecurityIcon />
                <span>Secured with 256-bit SSL encryption</span>
              </div>
            </div>

            {/* Help Section */}
            <div className="admin-help-section">
              <p className="admin-help-title">Need help?</p>
              <button type="button" className="admin-contact-link" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}>Contact Support</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon Components
const EmailIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 2.625V11.375C0 12.4102 0.839844 13.25 1.875 13.25H12.125C13.1602 13.25 14 12.4102 14 11.375V2.625C14 1.58984 13.1602 0.75 12.125 0.75H1.875C0.839844 0.75 0 1.58984 0 2.625ZM1.75 3.5L7 7.4375L12.25 3.5V11.375H1.75V3.5ZM12.25 2.1875L7 6.125L1.75 2.1875H12.25Z" fill="#949FB3"/>
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 0C4.34 0 3 1.34 3 3V4H2C1.45 4 1 4.45 1 5V12C1 12.55 1.45 13 2 13H10C10.55 13 11 12.55 11 12V5C11 4.45 10.55 4 10 4H9V3C9 1.34 7.66 0 6 0ZM6 1.5C7.1 1.5 8 2.4 8 3.5V4H4V3.5C4 2.4 4.9 1.5 6 1.5ZM2 5H10V12H2V5ZM6 8C6.55 8 7 8.45 7 9C7 9.55 6.55 10 6 10C5.45 10 5 9.55 5 9C5 8.45 5.45 8 6 8Z" fill="#949FB3"/>
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM8 14C4.69 14 2 11.31 2 8C2 4.69 4.69 2 8 2C11.31 2 14 4.69 14 8C14 11.31 11.31 14 8 14ZM8 4C5.79 4 4 5.79 4 8C4 10.21 5.79 12 8 12C10.21 12 12 10.21 12 8C12 5.79 10.21 4 8 4ZM8 10C6.9 10 6 9.1 6 8C6 6.9 6.9 6 8 6C9.1 6 10 6.9 10 8C10 9.1 9.1 10 8 10Z" fill="#949FB3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM8 14C4.69 14 2 11.31 2 8C2 4.69 4.69 2 8 2C11.31 2 14 4.69 14 8C14 11.31 11.31 14 8 14ZM8 4C5.79 4 4 5.79 4 8C4 10.21 5.79 12 8 12C10.21 12 12 10.21 12 8C12 5.79 10.21 4 8 4ZM8 10C6.9 10 6 9.1 6 8C6 6.9 6.9 6 8 6C9.1 6 10 6.9 10 8C10 9.1 9.1 10 8 10ZM2.71 1.29L1.29 2.71L4.59 6.01C4.21 6.79 4 7.65 4 8.5C4 10.71 5.79 12.5 8 12.5C8.85 12.5 9.71 12.29 10.49 11.91L13.79 15.21L15.21 13.79L2.71 1.29Z" fill="#949FB3"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 7C0 6.45 0.45 6 1 6H9.59L6.29 2.71C5.9 2.32 5.9 1.69 6.29 1.3C6.68 0.91 7.31 0.91 7.7 1.3L12.7 6.3C13.09 6.69 13.09 7.32 12.7 7.71L7.7 12.71C7.31 13.1 6.68 13.1 6.29 12.71C5.9 12.32 5.9 11.69 6.29 11.3L9.59 8H1C0.45 8 0 7.55 0 7Z" fill="white"/>
  </svg>
);

const LockSecurityIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 0C4.34 0 3 1.34 3 3V4H2C1.45 4 1 4.45 1 5V10C1 10.55 1.45 11 2 11H10C10.55 11 11 10.55 11 10V5C11 4.45 10.55 4 10 4H9V3C9 1.34 7.66 0 6 0ZM6 1.5C7.1 1.5 8 2.4 8 3.5V4H4V3.5C4 2.4 4.9 1.5 6 1.5ZM2 5H10V10H2V5ZM6 6.5C5.45 6.5 5 6.95 5 7.5C5 8.05 5.45 8.5 6 8.5C6.55 8.5 7 8.05 7 7.5C7 6.95 6.55 6.5 6 6.5Z" fill="#64748B"/>
  </svg>
);

export default AdminLogin;

